import React from "react";
import { inject, observer } from "mobx-react";
import { reaction, IReactionDisposer } from "mobx";
import { IDistrict, ISchool } from "../models/apiDataStructures";
import { SiSelect } from "./SiSelect";
import Button from "@mui/material/Button";
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { User } from "../models/user";
import { api } from "../lib/api";
import { userStore } from "../stores";
import { UserStore } from "../stores/UserStore";
import { Loader } from "./Loader";
import {
    IDistrictSchoolsResponse,
    IDistrictsResponse
} from "../models/apiResponses";


interface ISiAppAdminModalProps {
    userStore?: UserStore;
    open?: boolean;
    handleClose?: Function;
}

interface ISiAppAdminModalState {
    activeDistrict?: number | undefined;
    activeSchool?: number | undefined;
    districts?: IDistrict[] | undefined;
    user?: User | undefined;
    schools?: ISchool[] | undefined;
    loadingSchools: boolean
    loadingDistricts: boolean
    mounted: boolean
}

export const SiAppAdminModal = inject("userStore")(observer(class AppAdminModal extends React.Component<ISiAppAdminModalProps, ISiAppAdminModalState> {

    private userStore: UserStore;
    private schoolUpdateReaction: IReactionDisposer | null = null;

    constructor(props) {
        super(props)

        this.state = {
            loadingDistricts: false,
            loadingSchools: false,
            mounted: false
        }

        if (!props.userStore) {
            throw new Error("User store is not defined")
        }
        this.userStore = props.userStore;
    }

    componentDidMount() {
        this.setState({
            user: this.userStore.user
        })
        this.schoolUpdateReaction = reaction(
            () => this.state.activeDistrict,
            () => {
                this.loadSchoolsForDistrict(this.state.activeDistrict);
            }
        );
    }

    componentWillUnmount() {
        if(this.schoolUpdateReaction) this.schoolUpdateReaction();
    }

    componentDidUpdate(prevProps) {
        if (this.props.userStore?.adminModalOpen && !this.state.mounted) {

            if (!this.userStore.user.isAppAdmin) {
                console.error("Access denied.")
                this.closeResetModal();
            } else {
                this.loadDistricts();
                this.setState({ mounted: true })
            }
        }
    }

    async loadDistricts() {
        this.setState({ loadingDistricts: true })

        try {
            let districts = await api.find<IDistrictsResponse>("districts");
            this.setState({ districts }, () => {
                if (this.state.districts?.length === 1) {
                    this.setState({ activeDistrict: this.state.districts[0].id })
                }
            })
        } catch (err) {
            throw err;
        } finally {
            this.setState({ loadingDistricts: false })
        }
    }

    async loadSchoolsForDistrict(district?: number) {
        this.setState({ loadingSchools: true })
        try {
            let schools = await api.find<IDistrictSchoolsResponse>(
                `district-schools/${district}`
            );
            this.setState({ schools }, () => {
                if (this.state.schools?.length === 1) {
                    this.setState({
                        activeSchool: this.state.schools[0].id
                    })
                }
            })

        } catch (err) {
            throw err;
        } finally {
            this.setState({ loadingSchools: false })
        }
    }

    commitChanges = () => {
        if (!this.state.districts || !this.state.schools) return

        let newDistrict: IDistrict | undefined = undefined;
        let newSchool: ISchool | undefined = undefined;

        if (this.state.activeDistrict) {
            newDistrict = this.state.districts.find(
                obj => { return obj.id === this.state.activeDistrict })
        }

        if (this.state.activeSchool) {
            newSchool = this.state.schools.find(
                obj => { return obj.id === this.state.activeSchool })
        }

        this.userStore.setOverrideOrganizations(newSchool, newDistrict);
        this.closeResetModal();
    }

    closeResetModal = () => {
        this.resetState();
        this.userStore.closeAdminModal();
    }

    resetState = () => {
        this.setState({
            loadingDistricts: false,
            loadingSchools: false,
            mounted: false,
        })
    }

    render() {
        const { open } = this.props;
        const { districts, schools, loadingDistricts, loadingSchools, activeDistrict, activeSchool } = this.state;

        return (
            <Dialog
                open={this.userStore.adminModalOpen}
                onClose={this.closeResetModal}
                maxWidth={"sm"}
                fullWidth={true}
            >
                <DialogTitle>Admin Display Override</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <SiSelect label="Set active district">
                                <select
                                    onChange={event => {
                                        this.setState({ activeDistrict: parseInt(event.target.value) })
                                    }}
                                    value={activeDistrict}
                                >
                                    <option value="0">All Districts</option>

                                    <>
                                        {districts &&
                                            districts.map((district, index) => (
                                                <option value={district.id} key={index}>
                                                    {district.name}
                                                </option>
                                            ))}
                                    </>
                                </select>
                            </SiSelect>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SiSelect label="Set active school">
                                <select
                                    onChange={event => {
                                        this.setState({ activeSchool: parseInt(event.target.value) })
                                    }}
                                    value={activeSchool}
                                >
                                    <option value="0">All Schools</option>

                                    <>
                                        {schools &&
                                            schools.map((school, index) => (
                                                <option value={school.id} key={index}>
                                                    {school.name}
                                                </option>
                                            ))}
                                    </>
                                </select>
                            </SiSelect>
                        </Grid>
                    </Grid>

                </DialogContent>
                <DialogActions>
                    <Button onClick={this.closeResetModal} color="error">Cancel</Button>
                    <Button onClick={this.commitChanges}
                    >Save Changes</Button>
                </DialogActions>
            </Dialog >
        )
    }
}))